/**
 * Utility functions to match job locations with company addresses
 * and build full addresses for use in job schemas
 */

/**
 * City name mappings for Swedish/Finnish variants
 * Maps all variants to canonical English name for comparison
 */
const CITY_NAME_MAPPINGS: Record<string, string> = {
  // Helsinki variants
  'helsinki': 'helsinki',
  'helsingfors': 'helsinki',
  // Tampere variants
  'tampere': 'tampere',
  'tammerfors': 'tampere',
  // Turku variants
  'turku': 'turku',
  'åbo': 'turku',
  'abo': 'turku',
  // Oulu variants
  'oulu': 'oulu',
  'uleåborg': 'oulu',
  'uleaborg': 'oulu',
  // Espoo variants
  'espoo': 'espoo',
  'esbo': 'espoo',
  // Vantaa variants
  'vantaa': 'vantaa',
  'vanda': 'vantaa',
  // Vaasa variants
  'vaasa': 'vaasa',
  'vasa': 'vaasa',
  // Other cities
  'lahti': 'lahti',
  'kuopio': 'kuopio',
  'jyväskylä': 'jyvaskyla',
  'jyvaskyla': 'jyvaskyla',
  'pori': 'pori',
  'björneborg': 'pori',
  'bjorneborg': 'pori',
  'lappeenranta': 'lappeenranta',
  'villmanstrand': 'lappeenranta',
  'kotka': 'kotka',
  'joensuu': 'joensuu',
  'rovaniemi': 'rovaniemi',
  'seinäjoki': 'seinajoki',
  'seinajoki': 'seinajoki',
  'mikkeli': 'mikkeli',
  'hämeenlinna': 'hameenlinna',
  'hameenlinna': 'hameenlinna',
};

/**
 * Normalize city name to canonical form for comparison
 * Maps Swedish/Finnish variants to canonical English names
 */
export function normalizeCityName(cityName: string): string {
  if (!cityName) return '';
  
  const normalized = cityName
    .trim()
    .toLowerCase()
    // Normalize Unicode characters (remove diacritics)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritics
    // Handle Finnish/Swedish specific characters
    .replace(/[äå]/g, 'a')
    .replace(/[ö]/g, 'o')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    // Remove common punctuation
    .replace(/[.,\-_]/g, '')
    .trim();
  
  // Map to canonical city name if available
  return CITY_NAME_MAPPINGS[normalized] || normalized;
}

/**
 * Normalize location name for comparison
 * Removes accents, normalizes whitespace, handles special characters
 */
function normalizeLocationName(name: string): string {
  if (!name) return '';
  
  return name
    .trim()
    .toLowerCase()
    // Normalize Unicode characters (remove diacritics)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritics
    // Handle Finnish/Swedish specific characters
    .replace(/[äå]/g, 'a')
    .replace(/[ö]/g, 'o')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    // Remove common punctuation
    .replace(/[.,\-_]/g, '')
    .trim();
}

/**
 * Check if a job location matches a company address
 * Returns true if job location matches company address (e.g., "Helsinki, Finland" matches address containing "Helsinki")
 * Checks addressFi, addressSv, and registry address fields
 * Uses normalized comparison to handle accents, whitespace, and special characters
 */
export function matchesLocation(
  jobLocation: string,
  companyData: {
    address_fi?: string;
    address_sv?: string;
    registryCity?: string;
    registryStreet?: string;
    registryPostCode?: string;
    registryBuildingNumber?: string;
  } | null
): boolean {
  if (!companyData) return false;
  
  // Extract city from job location (e.g., "Helsinki, Finland" -> "Helsinki")
  const jobCityRaw = jobLocation.split(',')[0]?.trim();
  if (!jobCityRaw) return false;
  
  // Normalize the job city name to canonical form
  const jobCityCanonical = normalizeCityName(jobCityRaw);
  if (!jobCityCanonical) return false;
  
  // Check all address fields
  const addressesToCheck = [
    companyData.address_fi,
    companyData.address_sv,
    companyData.registryCity,
    companyData.registryStreet, // Sometimes street contains city name
  ].filter(Boolean);
  
  // Also construct full registry address if available
  if (companyData.registryStreet && companyData.registryCity) {
    const fullRegistryAddress = `${companyData.registryStreet} ${companyData.registryBuildingNumber || ''} ${companyData.registryPostCode || ''} ${companyData.registryCity}`.trim();
    addressesToCheck.push(fullRegistryAddress);
  }
  
  // Check if any normalized address contains a city that matches the canonical job city
  return addressesToCheck.some(addr => {
    if (!addr) return false;
    const normalizedAddr = normalizeLocationName(addr);
    
    // Check if the normalized address contains the canonical city name
    if (normalizedAddr.includes(jobCityCanonical)) {
      return true;
    }
    
    // Also check if any city name in the address maps to the same canonical city
    // Extract potential city names from the address (words that might be cities)
    const words = normalizedAddr.split(/\s+/);
    for (const word of words) {
      const wordCanonical = normalizeCityName(word);
      if (wordCanonical === jobCityCanonical) {
        return true;
      }
    }
    
    return false;
  });
}

/**
 * Build a full postal address from company registry fields
 * Returns a PostalAddress object suitable for schema.org
 */
export function buildFullCompanyAddress(
  companyData: {
    registryStreet?: string;
    registryBuildingNumber?: string;
    registryPostCode?: string;
    registryCity?: string;
    address_fi?: string;
    address_sv?: string;
  } | null
): {
  streetAddress?: string;
  addressLocality?: string;
  postalCode?: string;
  addressCountry: string;
} | null {
  if (!companyData) return null;
  
  // Prefer registry address fields if available (most complete)
  if (companyData.registryStreet && companyData.registryCity) {
    const streetAddressParts: string[] = [];
    
    if (companyData.registryStreet) {
      streetAddressParts.push(companyData.registryStreet);
    }
    if (companyData.registryBuildingNumber) {
      streetAddressParts.push(companyData.registryBuildingNumber);
    }
    
    return {
      streetAddress: streetAddressParts.join(' ').trim() || undefined,
      addressLocality: companyData.registryCity,
      postalCode: companyData.registryPostCode || undefined,
      addressCountry: 'FI', // All registry addresses in Finland
    };
  }
  
  // Fallback to address_fi or address_sv if registry fields not available
  const address = companyData.address_fi || companyData.address_sv;
  if (address) {
    // Try to parse address string (format may vary)
    // Common formats: "Street, City" or "Street City" or "Street, PostalCode City"
    const parts = address.split(',').map(p => p.trim());
    
    if (parts.length >= 2) {
      // Format: "Street, City" or "Street, PostalCode City"
      const streetPart = parts[0];
      const cityPart = parts.slice(1).join(', ');
      
      // Try to extract postal code if present (usually 5 digits)
      const postalCodeMatch = cityPart.match(/\b\d{5}\b/);
      const postalCode = postalCodeMatch ? postalCodeMatch[0] : undefined;
      const addressLocality = cityPart.replace(/\b\d{5}\b/, '').trim();
      
      return {
        streetAddress: streetPart || undefined,
        addressLocality: addressLocality || undefined,
        postalCode,
        addressCountry: 'FI',
      };
    } else if (parts.length === 1) {
      // Single part - might be just city or just street
      // Check if it looks like a city name (capitalized, no numbers)
      const isLikelyCity = /^[A-ZÄÖÅ]/.test(parts[0]);

      return {
        streetAddress: isLikelyCity ? undefined : parts[0],
        addressLocality: isLikelyCity ? parts[0] : undefined,
        addressCountry: 'FI',
      };
    }
  }

  return null;
}

/**
 * Renders a {@link buildFullCompanyAddress} result as a single human-readable line
 * (e.g. "Mannerheimintie 12, 00100 Helsinki") for map popups and address display.
 * Requires a street component — a bare city (e.g. "Helsinki" with no street) is not a
 * real address, so this returns null for it. Callers can use a non-null result as the
 * "does this company/job have a real address to put on a map" signal.
 */
export function formatPostalAddressLine(
  address: ReturnType<typeof buildFullCompanyAddress>,
): string | null {
  if (!address || !address.streetAddress) return null;
  const cityLine = [address.postalCode, address.addressLocality].filter(Boolean).join(' ');
  const line = [address.streetAddress, cityLine].filter(Boolean).join(', ').trim();
  return line || null;
}
